/**
 * 注册来源归因（UTM / referrer）
 *
 * 官网所有 CTA 都带 utm_source=website&utm_medium=cta&utm_campaign=<campaign>
 * （见 site/src/data/site.ts 的 appStoreLink / registerLink），
 * 但 app 之前完全不读这些参数，来源在落地页就丢了。
 * 这里在 app 启动时抓一次存 localStorage，注册时随注册请求带给后端，
 * 落到 ad_user 的 utm_source / utm_medium / utm_campaign / referrer / landing_page。
 *
 * 规则（2026-10-19 调整，避免「官网 header 登录」把真实来源冲掉）：
 * - 官网自己 CTA 的 utm（utm_source=website）**不算获客来源**，不能覆盖已有记录
 * - 只有「外部」utm（广告 / 邮件 / 联盟）才允许覆盖
 * - 首次是直接访问/站内点击（没有外部来源）时，后来从外部 referrer 进来会补一次
 * - 30 天有效期（与后端 /alliance/{id} 的 aff cookie 一致）
 * - 只记来源参数与落地页，不采集任何用户身份信息
 */
const STORAGE_KEY = 'hf_attribution'
const MAX_AGE = 30 * 24 * 60 * 60 * 1000
// 官网自己的 CTA 统一用 utm_source=website，它不是获客来源
const INTERNAL_UTM_SOURCE = 'website'

function ownHost(url) {
  try {
    return /(^|\.)honestfulphilment\.com$/i.test(new URL(url).hostname)
  } catch (e) {
    return false
  }
}

/** 外部 referrer（站内跳转、空值都返回 ''） */
function externalReferrer(ref) {
  return ref && !ownHost(ref) ? ref : ''
}

function load() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const data = JSON.parse(raw)
    if (!data || !data.ts || Date.now() - data.ts > MAX_AGE) return null
    return data
  } catch (e) {
    return null // 隐私模式 / 存储被禁用
  }
}

function save(data) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(data))
  } catch (e) {
    // 存不下就算了，不影响注册
  }
}

function queryParam(search, name) {
  try {
    const value = new URLSearchParams(search || '').get(name)
    return value ? value.trim() : ''
  } catch (e) {
    return ''
  }
}

/** app 启动时调用：记录首次外部来源，站内 CTA 不覆盖 */
export function captureAttribution() {
  const search = window.location.search || ''
  const utmSource = queryParam(search, 'utm_source')
  const utmMedium = queryParam(search, 'utm_medium')
  const utmCampaign = queryParam(search, 'utm_campaign')
  const hasUtm = !!(utmSource || utmMedium || utmCampaign)
  const internalUtm = utmSource.toLowerCase() === INTERNAL_UTM_SOURCE
  const ref = document.referrer || ''
  const extRef = externalReferrer(ref)
  const landingPage = window.location.pathname + window.location.search
  const stored = load()

  // 1) 首次访问：记下当前来源（可能是站内 CTA、直接访问或外部来源）
  if (!stored) {
    const data = { utmSource, utmMedium, utmCampaign, referrer: ref, landingPage, ts: Date.now() }
    save(data)
    return data
  }

  // 2) 外部 utm（广告/邮件/联盟）可以覆盖；官网自己的 website/cta/* 不能
  if (hasUtm && !internalUtm) {
    const data = { utmSource, utmMedium, utmCampaign, referrer: ref || stored.referrer || '', landingPage, ts: Date.now() }
    save(data)
    return data
  }

  // 3) 首次只有直接访问/站内来源，后来从外部 referrer 进来（如从 Gmail 点回来）→ 补一次
  if ((!stored.referrer || ownHost(stored.referrer)) && extRef) {
    const data = { ...stored, referrer: extRef, landingPage, ts: Date.now() }
    save(data)
    return data
  }

  // 4) 其余保持首次外部来源
  return stored
}

/** 注册请求里带上：空值返回 undefined，axios 会自动丢掉这些字段 */
export function getAttribution() {
  const data = load()
  if (!data) return {}
  return {
    utmSource: data.utmSource || undefined,
    utmMedium: data.utmMedium || undefined,
    utmCampaign: data.utmCampaign || undefined,
    referrer: data.referrer || undefined,
    landingPage: data.landingPage || undefined
  }
}

export default { captureAttribution, getAttribution }
